export type ContactConfidenceLevel = 'low' | 'medium' | 'high' | 'unknown';
export type ContactConfidenceStatus = 'candidate' | 'confirmed' | 'rejected' | 'unknown';
export type ProfileHintKind =
  | 'phone_number'
  | 'email_address'
  | 'person_name'
  | 'property_address'
  | 'project_term'
  | 'lead_form_id'
  | 'calendar_attendee'
  | 'gmail_thread_id'
  | 'gmail_message_id'
  | 'call_sid'
  | 'transcript_id';
export type ProfileSourceKind = 'call_transcript' | 'email' | 'calendar';

export type CommunicationProfileHintSummary = {
  kind: ProfileHintKind;
  value: string | null;
  confidence: Exclude<ContactConfidenceLevel, 'unknown'>;
  status: Exclude<ContactConfidenceStatus, 'unknown'>;
  observedAt: string;
  sourceKind: ProfileSourceKind;
  sourceRefCount: number;
};

export type CommunicationContactConfidenceSummary = {
  level: ContactConfidenceLevel;
  status: ContactConfidenceStatus;
  contactableHintCount: number;
  confirmedContactableHintCount: number;
  candidateContactableHintCount: number;
  rejectedContactableHintCount: number;
  sourceKinds: ProfileSourceKind[];
  strongestHints: CommunicationProfileHintSummary[];
};

export type CommunicationHintKindSummary = {
  kind: ProfileHintKind;
  total: number;
  visibleValueCount: number;
  confidence: ContactConfidenceLevel;
  status: ContactConfidenceStatus;
};

export type CommunicationProfileSummaryResponse = {
  profileHints: CommunicationProfileHintSummary[];
  contactConfidence: CommunicationContactConfidenceSummary;
  hintKinds: CommunicationHintKindSummary[];
  meta: {
    source: 'customer_profile_identity_hints_store';
    orgScoped: true;
    persistedHintsOnly: true;
    liveProviderCalls: false;
    rawProviderBodiesExposed: false;
    rawProviderIdentifiersExposed: false;
    broadCustomerGraphMerge: false;
  };
};

export type CommunicationProfileSummaryDisplay = {
  status: 'unavailable' | 'empty' | 'ready';
  title: string;
  detail: string;
  confidenceLabel: string;
  confidenceBadge: string;
  contactableCountLabel: string;
  sourceLabel: string;
  safetyLabel: string;
  visibleHints: Array<{
    key: string;
    label: string;
    value: string;
    detail: string;
  }>;
};

const HINT_LABELS: Record<ProfileHintKind, string> = {
  phone_number: 'Phone',
  email_address: 'Email',
  person_name: 'Name',
  property_address: 'Property address',
  project_term: 'Project term',
  lead_form_id: 'Lead form clue',
  calendar_attendee: 'Calendar attendee clue',
  gmail_thread_id: 'Gmail clue',
  gmail_message_id: 'Gmail clue',
  call_sid: 'Call metadata clue',
  transcript_id: 'Transcript clue',
};

const SOURCE_LABELS: Record<ProfileSourceKind, string> = {
  call_transcript: 'call metadata/transcript',
  email: 'email metadata',
  calendar: 'calendar metadata',
};

const VALID_HINT_KINDS = new Set(Object.keys(HINT_LABELS));
const VALID_SOURCE_KINDS = new Set(Object.keys(SOURCE_LABELS));
const VALID_CONFIDENCE = new Set(['low', 'medium', 'high']);
const VALID_HINT_STATUS = new Set(['candidate', 'confirmed', 'rejected']);

export function buildCommunicationProfileSummaryDisplay(
  response: CommunicationProfileSummaryResponse | null | undefined,
): CommunicationProfileSummaryDisplay {
  if (!response) {
    return unavailableProfileSummaryDisplay();
  }

  const profileHints = Array.isArray(response.profileHints) ? response.profileHints.filter(isSafeDisplayHint) : [];
  const contactConfidence = response.contactConfidence ?? {
    level: 'unknown',
    status: 'unknown',
    contactableHintCount: 0,
    sourceKinds: [],
  };
  const contactableCount = typeof contactConfidence.contactableHintCount === 'number' ? contactConfidence.contactableHintCount : 0;
  const visibleHints = profileHints.slice(0, 6).map((hint, index) => ({
    key: `${hintLabelFor(hint.kind)}:${typeof hint.observedAt === 'string' ? hint.observedAt : 'unknown'}:${index}`,
    label: hintLabelFor(hint.kind),
    value: safeVisibleHintValue(hint.value),
    detail: `${capitalize(safeStatusValue(hint.confidence))} confidence · ${safeStatusValue(hint.status)} · from ${sourceLabelFor(hint.sourceKind)}`,
  }));

  if (profileHints.length === 0) {
    return {
      status: 'empty',
      title: 'No customer profile hints yet',
      detail: 'Persisted Engine identity hints will appear here after a communication produces safe contact evidence.',
      confidenceLabel: 'Unknown',
      confidenceBadge: 'No hints',
      contactableCountLabel: '0 contactable hints',
      sourceLabel: 'Persisted hints only',
      safetyLabel: 'No customer merge or broad graph resolution is being shown.',
      visibleHints,
    };
  }

  return {
    status: 'ready',
    title: 'Customer profile hints',
    detail: 'Shows admin-safe persisted identity hints and contact confidence only; source records and provider identifiers stay hidden.',
    confidenceLabel: capitalize(safeContactConfidence(contactConfidence.level)),
    confidenceBadge: `${capitalize(safeContactStatus(contactConfidence.status))} contact`,
    contactableCountLabel: `${contactableCount} contactable hint${contactableCount === 1 ? '' : 's'}`,
    sourceLabel: sourceKindsLabel(Array.isArray(contactConfidence.sourceKinds) ? contactConfidence.sourceKinds : []),
    safetyLabel: 'Persisted hints only · raw source/provider identifiers redacted · no live provider lookup.',
    visibleHints,
  };
}

export function profileConfidenceTone(level: ContactConfidenceLevel): 'emerald' | 'amber' | 'slate' {
  if (level === 'high') return 'emerald';
  if (level === 'medium' || level === 'low') return 'amber';
  return 'slate';
}

function unavailableProfileSummaryDisplay(): CommunicationProfileSummaryDisplay {
  return {
    status: 'unavailable',
    title: 'Customer profile hints unavailable',
    detail: 'The optional contact-confidence summary could not load. Communication nodes can still be reviewed.',
    confidenceLabel: 'Unknown',
    confidenceBadge: 'Unavailable',
    contactableCountLabel: '—',
    sourceLabel: 'Optional persisted-hint status only',
    safetyLabel: 'No live provider lookup was run for this card.',
    visibleHints: [],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isSafeDisplayHint(value: unknown): value is CommunicationProfileHintSummary {
  if (!isRecord(value)) return false;
  return (
    typeof value.kind === 'string' && VALID_HINT_KINDS.has(value.kind) &&
    (typeof value.value === 'string' || value.value === null) &&
    typeof value.confidence === 'string' && VALID_CONFIDENCE.has(value.confidence) &&
    typeof value.status === 'string' && VALID_HINT_STATUS.has(value.status) &&
    typeof value.observedAt === 'string' && value.observedAt.trim().length > 0 &&
    typeof value.sourceKind === 'string' && VALID_SOURCE_KINDS.has(value.sourceKind) &&
    typeof value.sourceRefCount === 'number'
  );
}

function hintLabelFor(kind: ProfileHintKind): string {
  return HINT_LABELS[kind];
}

function sourceLabelFor(kind: ProfileSourceKind): string {
  return SOURCE_LABELS[kind];
}

function safeVisibleHintValue(value: string | null): string {
  return typeof value === 'string' && value.trim() ? value.trim() : 'Stored identifier redacted';
}

function safeStatusValue(value: string): string {
  return value.trim().toLowerCase();
}

function safeContactConfidence(value: unknown): ContactConfidenceLevel {
  if (typeof value !== 'string') return 'unknown';
  const normalized = value.trim().toLowerCase();
  return normalized === 'low' || normalized === 'medium' || normalized === 'high' || normalized === 'unknown' ? normalized : 'unknown';
}

function safeContactStatus(value: unknown): ContactConfidenceStatus {
  if (typeof value !== 'string') return 'unknown';
  const normalized = value.trim().toLowerCase();
  return normalized === 'candidate' || normalized === 'confirmed' || normalized === 'rejected' || normalized === 'unknown' ? normalized : 'unknown';
}

function sourceKindsLabel(sourceKinds: ProfileSourceKind[]): string {
  const safeKinds = sourceKinds.filter(kind => VALID_SOURCE_KINDS.has(kind));
  if (safeKinds.length === 0) return 'No contactable source hints yet';
  return `Contactable hints from ${safeKinds.map(kind => sourceLabelFor(kind)).join(', ')}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
